import { useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import CardTile from "@/components/board/CardTile";
import { toast } from "sonner";
import { Zap, Calendar as CalIcon, UserCheck, Archive } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * 2x2 priority matrix view (Urgent / Important). Cards can be dragged between
 * quadrants — the drop updates is_urgent + is_important on the card.
 *
 * Quadrants:
 *   Q1 top-left:  urgent + important   → "Hacer ahora"
 *   Q2 top-right: !urgent + important  → "Programar"
 *   Q3 bot-left:  urgent + !important  → "Delegar"
 *   Q4 bot-right: !urgent + !important → "Posponer"
 */
const QUADRANTS = [
  { id: "do",       row: 0, col: 0, urgent: true,  important: true,
    title: "Hacer ahora", subtitle: "Urgente · Importante",
    icon: Zap, accent: "rose",
    classes: "bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300" },
  { id: "plan",     row: 0, col: 1, urgent: false, important: true,
    title: "Programar", subtitle: "Importante · No urgente",
    icon: CalIcon, accent: "blue",
    classes: "bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300" },
  { id: "delegate", row: 1, col: 0, urgent: true,  important: false,
    title: "Delegar", subtitle: "Urgente · No importante",
    icon: UserCheck, accent: "amber",
    classes: "bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300" },
  { id: "park",     row: 1, col: 1, urgent: false, important: false,
    title: "Posponer", subtitle: "No urgente · No importante",
    icon: Archive, accent: "slate",
    classes: "bg-slate-500/10 border-slate-500/30 text-slate-700 dark:text-slate-300" },
];

function bucketKey(card) {
  const u = !!card.is_urgent;
  const i = !!card.is_important;
  if (u && i) return "do";
  if (!u && i) return "plan";
  if (u && !i) return "delegate";
  return "park";
}

export default function BoardMatrix({ boardId, cards, labels, lists, onOpenCard }) {
  const qc = useQueryClient();
  const listById = useMemo(() => Object.fromEntries((lists || []).map(l => [l.list_id, l])), [lists]);

  const byBucket = useMemo(() => {
    const groups = { do: [], plan: [], delegate: [], park: [] };
    for (const c of cards) groups[bucketKey(c)].push(c);
    return groups;
  }, [cards]);

  const move = useMutation({
    mutationFn: async ({ card, urgent, important }) =>
      (await api.patch(`/cards/${card.card_id}`, { is_urgent: urgent, is_important: important })).data,
    onMutate: async ({ card, urgent, important }) => {
      qc.setQueryData(["board-full", boardId], (old) => {
        if (!old) return old;
        return {
          ...old,
          cards: old.cards.map(c =>
            c.card_id === card.card_id ? { ...c, is_urgent: urgent, is_important: important } : c
          ),
        };
      });
    },
    onError: () => {
      toast.error("No se pudo actualizar la prioridad");
      qc.invalidateQueries({ queryKey: ["board-full", boardId] });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["board-full", boardId] });
    },
  });

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    const target = QUADRANTS.find(q => q.id === destination.droppableId);
    const card = cards.find(c => c.card_id === draggableId);
    if (!card || !target) return;
    move.mutate({ card, urgent: target.urgent, important: target.important });
  };

  return (
    <div className="h-full overflow-auto kanban-scroll p-4 md:p-6" data-testid="board-matrix">
      <div className="max-w-6xl mx-auto">
        <div className="mb-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Vista alternativa</p>
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Matriz de prioridades</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Arrastra una tarjeta a otro cuadrante para reasignar su urgencia e importancia.
          </p>
        </div>

        {/* Axis labels */}
        <div className="hidden md:grid grid-cols-[1.5rem_1fr] gap-x-2 mb-1">
          <div />
          <div className="grid grid-cols-2 gap-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">Urgente</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">No urgente</p>
          </div>
        </div>

        <DragDropContext onDragEnd={onDragEnd}>
          <div className="md:grid md:grid-cols-[1.5rem_1fr] md:gap-x-2 space-y-3 md:space-y-0">
            {/* Y axis labels */}
            <div className="hidden md:flex flex-col">
              <div className="flex-1 grid place-items-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground rotate-180" style={{ writingMode: "vertical-rl" }}>Importante</p>
              </div>
              <div className="flex-1 grid place-items-center">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground rotate-180" style={{ writingMode: "vertical-rl" }}>No importante</p>
              </div>
            </div>

            {/* 2x2 grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {QUADRANTS.map((q) => (
                <Quadrant
                  key={q.id}
                  q={q}
                  cards={byBucket[q.id]}
                  labels={labels}
                  listById={listById}
                  onOpenCard={onOpenCard}
                />
              ))}
            </div>
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}

function Quadrant({ q, cards, labels, listById, onOpenCard }) {
  const Icon = q.icon;
  const { t } = useTranslation();
  return (
    <Droppable droppableId={q.id} type="MATRIX_CARD">
      {(p, s) => (
        <div
          ref={p.innerRef}
          {...p.droppableProps}
          data-testid={`matrix-quadrant-${q.id}`}
          className={cn(
            "rounded-2xl border-2 border-dashed p-3 md:p-4 min-h-[260px] flex flex-col transition-colors",
            q.classes,
            s.isDraggingOver && "bg-opacity-30 ring-2 ring-offset-2 ring-offset-transparent",
          )}
        >
          <div className="flex items-center gap-2 mb-3">
            <span className="h-8 w-8 rounded-lg bg-white/60 dark:bg-slate-900/60 grid place-items-center">
              <Icon className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <p className="font-display font-semibold leading-tight">{q.title}</p>
              <p className="text-[11px] opacity-80 leading-tight">{q.subtitle}</p>
            </div>
            <span className="text-xs font-semibold bg-white/60 dark:bg-slate-900/60 px-2 py-0.5 rounded-full">
              {cards.length}
            </span>
          </div>

          <div className="flex-1 space-y-2 overflow-y-auto kanban-scroll">
            {cards.map((card, idx) => (
              <Draggable draggableId={card.card_id} index={idx} key={card.card_id}>
                {(p3, s3) => (
                  <div
                    ref={p3.innerRef} {...p3.draggableProps} {...p3.dragHandleProps}
                    className={cn(s3.isDragging && "card-dragging")}
                    data-testid={`matrix-card-${card.card_id}`}
                  >
                    <CardTile
                      card={card}
                      labels={labels}
                      onClick={() => onOpenCard(card.card_id)}
                      meta={listById[card.list_id]?.name}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {p.placeholder}
            {cards.length === 0 && !s.isDraggingOver && (
              <p className="text-xs text-center opacity-60 italic py-6">{t("boardView.dropCards")}</p>
            )}
          </div>
        </div>
      )}
    </Droppable>
  );
}
